import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import {
  getDashboardStats,
  getContacts,
  getServices,
  getSettings,
  updateContact,
  updateSettings,
} from '../api';
import { toast } from '../hooks/use-toast';
import {
  LayoutDashboard,
  Users,
  Settings,
  LogOut,
  Phone,
  Mail,
  Package,
  Image,
  HelpCircle,
  CheckCircle,
  Clock,
} from 'lucide-react';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [settings, setSettings] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    loadData();
  }, [navigate]);

  const loadData = async () => {
    try {
      const [statsRes, contactsRes, settingsRes] = await Promise.all([
        getDashboardStats(),
        getContacts(),
        getSettings(),
      ]);
      setStats(statsRes.data);
      setContacts(contactsRes.data);
      setSettings(settingsRes.data);
    } catch (error) {
      toast({
        title: 'Ошибка',
        description: 'Не удалось загрузить данные.',
        variant: 'destructive',
      });
      if (error.response?.status === 401) {
        handleLogout();
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminEmail');
    navigate('/admin/login');
  };

  const handleContactStatusChange = async (contactId, newStatus) => {
    try {
      await updateContact(contactId, { status: newStatus });
      setContacts(
        contacts.map((c) => (c.id === contactId ? { ...c, status: newStatus } : c))
      );
      toast({
        title: 'Статус обновлен',
        description: 'Статус заявки успешно изменен.',
      });
    } catch (error) {
      toast({
        title: 'Ошибка',
        description: 'Не удалось обновить статус.',
        variant: 'destructive',
      });
    }
  };

  const handleSettingsUpdate = async (e) => {
    e.preventDefault();
    try {
      await updateSettings(settings);
      toast({
        title: 'Настройки сохранены',
        description: 'Настройки сайта успешно обновлены.',
      });
    } catch (error) {
      toast({
        title: 'Ошибка',
        description: 'Не удалось сохранить настройки.',
        variant: 'destructive',
      });
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Загрузка...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Админ-панель - Bell</title>
      </Helmet>
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <header className="bg-white shadow-sm border-b">
          <div className="container mx-auto px-4 py-4 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img
                src="/logo.png"
                alt="Mandarin"
                className="w-12 h-12 object-contain"
              />
              <div>
                <h1 className="text-xl font-bold text-gray-900">Админ-панель</h1>
                <p className="text-sm text-gray-500">
                  {localStorage.getItem('adminEmail')}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <Link
                to="/"
                className="text-gray-600 hover:text-orange-600 transition-colors"
              >
                На сайт
              </Link>
              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 text-gray-600 hover:text-red-600 transition-colors"
              >
                <LogOut className="w-5 h-5" />
                <span>Выйти</span>
              </button>
            </div>
          </div>
        </header>

        <div className="container mx-auto px-4 py-8">
          {/* Navigation Tabs */}
          <div className="flex space-x-4 mb-8 border-b">
            <button
              onClick={() => setActiveTab('dashboard')}
              className={`flex items-center space-x-2 px-4 py-3 font-medium transition-colors border-b-2 ${
                activeTab === 'dashboard'
                  ? 'text-orange-600 border-orange-600'
                  : 'text-gray-600 border-transparent hover:text-orange-600'
              }`}
            >
              <LayoutDashboard className="w-5 h-5" />
              <span>Панель</span>
            </button>
            <button
              onClick={() => setActiveTab('contacts')}
              className={`flex items-center space-x-2 px-4 py-3 font-medium transition-colors border-b-2 ${
                activeTab === 'contacts'
                  ? 'text-orange-600 border-orange-600'
                  : 'text-gray-600 border-transparent hover:text-orange-600'
              }`}
            >
              <Users className="w-5 h-5" />
              <span>Заявки ({stats?.newContacts})</span>
            </button>
            <button
              onClick={() => setActiveTab('settings')}
              className={`flex items-center space-x-2 px-4 py-3 font-medium transition-colors border-b-2 ${
                activeTab === 'settings'
                  ? 'text-orange-600 border-orange-600'
                  : 'text-gray-600 border-transparent hover:text-orange-600'
              }`}
            >
              <Settings className="w-5 h-5" />
              <span>Настройки</span>
            </button>
          </div>

          {/* Dashboard Tab */}
          {activeTab === 'dashboard' && (
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Статистика</h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-white rounded-xl p-6 shadow-sm border-2 border-orange-100">
                  <div className="flex items-center justify-between mb-2">
                    <Users className="w-8 h-8 text-orange-600" />
                    <span className="text-3xl font-bold text-gray-900">
                      {stats?.totalContacts}
                    </span>
                  </div>
                  <p className="text-gray-600">Всего заявок</p>
                  <p className="text-sm text-orange-600 mt-1">
                    {stats?.newContacts} новых
                  </p>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-sm border-2 border-blue-100">
                  <div className="flex items-center justify-between mb-2">
                    <Package className="w-8 h-8 text-blue-600" />
                    <span className="text-3xl font-bold text-gray-900">
                      {stats?.totalServices}
                    </span>
                  </div>
                  <p className="text-gray-600">Услуг</p>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-sm border-2 border-purple-100">
                  <div className="flex items-center justify-between mb-2">
                    <Image className="w-8 h-8 text-purple-600" />
                    <span className="text-3xl font-bold text-gray-900">
                      {stats?.totalGalleryImages}
                    </span>
                  </div>
                  <p className="text-gray-600">Фото в галерее</p>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-sm border-2 border-orange-100">
                  <div className="flex items-center justify-between mb-2">
                    <HelpCircle className="w-8 h-8 text-orange-600" />
                    <span className="text-3xl font-bold text-gray-900">3</span>
                  </div>
                  <p className="text-gray-600">FAQ вопросов</p>
                </div>
              </div>
            </div>
          )}

          {/* Contacts Tab */}
          {activeTab === 'contacts' && (
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Заявки</h2>
              <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                          Имя
                        </th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                          Телефон
                        </th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                          Источник
                        </th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                          Дата
                        </th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                          Статус
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {contacts.map((contact) => (
                        <tr key={contact.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 text-sm text-gray-900">
                            {contact.name}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">
                            <a
                              href={`tel:${contact.phone}`}
                              className="text-orange-600 hover:text-orange-700"
                            >
                              {contact.phone}
                            </a>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600">
                            {contact.source}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600">
                            {new Date(contact.createdAt).toLocaleString('ru-RU')}
                          </td>
                          <td className="px-6 py-4">
                            <select
                              value={contact.status}
                              onChange={(e) =>
                                handleContactStatusChange(contact.id, e.target.value)
                              }
                              className={`px-3 py-1 rounded-full text-sm font-medium ${
                                contact.status === 'new'
                                  ? 'bg-blue-100 text-blue-700'
                                  : contact.status === 'contacted'
                                  ? 'bg-yellow-100 text-yellow-700'
                                  : 'bg-green-100 text-green-700'
                              }`}
                            >
                              <option value="new">Новая</option>
                              <option value="contacted">Связались</option>
                              <option value="completed">Завершена</option>
                            </select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Settings Tab */}
          {activeTab === 'settings' && settings && (
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Настройки сайта</h2>
              <div className="bg-white rounded-xl shadow-sm p-6">
                <form onSubmit={handleSettingsUpdate} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Название компании
                      </label>
                      <input
                        type="text"
                        value={settings.companyName}
                        onChange={(e) =>
                          setSettings({ ...settings, companyName: e.target.value })
                        }
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Телефон
                      </label>
                      <input
                        type="tel"
                        value={settings.phone}
                        onChange={(e) =>
                          setSettings({ ...settings, phone: e.target.value })
                        }
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email
                      </label>
                      <input
                        type="email"
                        value={settings.email}
                        onChange={(e) =>
                          setSettings({ ...settings, email: e.target.value })
                        }
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none"
                      />
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">SEO</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Meta Title
                        </label>
                        <input
                          type="text"
                          value={settings.seo.home.title}
                          onChange={(e) =>
                            setSettings({
                              ...settings,
                              seo: {
                                ...settings.seo,
                                home: { ...settings.seo.home, title: e.target.value },
                              },
                            })
                          }
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Meta Description
                        </label>
                        <textarea
                          value={settings.seo.home.description}
                          onChange={(e) =>
                            setSettings({
                              ...settings,
                              seo: {
                                ...settings.seo,
                                home: {
                                  ...settings.seo.home,
                                  description: e.target.value,
                                },
                              },
                            })
                          }
                          rows={3}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Keywords
                        </label>
                        <input
                          type="text"
                          value={settings.seo.home.keywords}
                          onChange={(e) =>
                            setSettings({
                              ...settings,
                              seo: {
                                ...settings.seo,
                                home: {
                                  ...settings.seo.home,
                                  keywords: e.target.value,
                                },
                              },
                            })
                          }
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <button
                      type="submit"
                      className="bg-orange-600 hover:bg-orange-700 text-white font-semibold px-6 py-3 rounded-lg transition-colors shadow-lg hover:shadow-xl"
                    >
                      Сохранить изменения
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default AdminDashboard;
